import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MessageSquare, Mail, Phone, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import AdminInquiryChat from './AdminInquiryChat';
import { isUnread, markSeen } from '../lib/unread';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminVisaInquiries = () => {
  const { adminAuth } = useOutletContext();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatInquiryId, setChatInquiryId] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      // Use server-side filtering for visa inquiries
      const res = await fetch(`${API_URL}/api/admin/inquiries?inquiry_type=visa`, { headers: { Authorization: `Basic ${adminAuth}` } });
      if (res.ok) {
        const data = await res.json();
        setInquiries(data);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [adminAuth]);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 15000); // light polling
    return () => clearInterval(timer.current);
  }, [load]);

  const openChat = (inq) => {
    markSeen('adm', inq.id, inq.last_activity);
    setChatInquiryId(inq.id);
    setInquiries((prev) => [...prev]); // re-render to clear badge
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Delete this visa inquiry and its conversation? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/inquiries/${id}`, { method: 'DELETE', headers: { Authorization: `Basic ${adminAuth}` } });
      if (res.ok) { toast.success('Inquiry deleted'); load(); }
      else toast.error('Failed to delete');
    } catch { toast.error('Delete failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64">Loading...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Visa Inquiries</h1>
        <p className="text-gray-600">Customers who applied for a visa. Chat & exchange documents.</p>
      </div>

      {inquiries.length === 0 ? (
        <Card className="border-dashed border-2"><CardContent className="p-12 text-center">
          <MessageSquare className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No visa inquiries yet</h3>
          <p className="text-gray-500">When a customer applies for a visa from the Visa page, it appears here.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="visa-inquiries-list">
          {inquiries.map((inq) => {
            const unread = isUnread('adm', inq, 'team');
            return (
              <Card key={inq.id} data-testid={`visa-inquiry-${inq.id}`} className={`hover:shadow-md transition-shadow ${unread ? 'ring-2 ring-amber-400' : ''}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900">{inq.name}</h3>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{inq.destination}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${inq.status === 'new' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{inq.status}</span>
                      {unread && <span data-testid={`unread-${inq.id}`} className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full font-semibold">New message</span>}
                      {inq.last_responder && (
                        <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium" title="Staff who replied">
                          <MessageSquare className="w-3 h-3" /> Replied by {(inq.responders && inq.responders.length > 1) ? inq.responders.join(', ') : inq.last_responder}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 text-xs text-gray-500 mt-1">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {inq.email}</span>
                      <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {inq.phone}</span>
                    </div>
                  </div>
                  <Button size="sm" onClick={() => openChat(inq)} data-testid={`open-visa-chat-${inq.id}`} className="bg-amber-600 hover:bg-amber-700 shrink-0">
                    <MessageSquare className="w-4 h-4 mr-1" /> Chat & Documents
                  </Button>
                  <button onClick={() => deleteInquiry(inq.id)} data-testid={`delete-visa-inquiry-${inq.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded shrink-0" title="Delete inquiry">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {chatInquiryId && (
        <AdminInquiryChat
          inquiryId={chatInquiryId}
          adminAuth={adminAuth}
          onClose={() => { setChatInquiryId(null); load(); }}
          onSent={load}
        />
      )}
    </div>
  );
};

export default AdminVisaInquiries;
